import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import { BoxGeometry, CylinderGeometry, Group, Mesh, MeshLambertMaterial } from 'three'

import { getVehicleById, LOCO_DEFAULT_SPEED, type Locomotive } from '@engine'
import { useGameStore } from '@stores'

const PIN_OFFSET = 0.12 // Radius of the eccentric
const ROD_LENGTH = 0.65 // Length of the driving rod (adapted to end behind the small wheels)
const ROD_CLEARANCE = 0.1 // Base X offset (coupling rod)
const MAIN_ROD_OFFSET = 0.02 // Extra X offset for the driving rod (to avoid intersecting the other)

const pinGeometry = new CylinderGeometry(0.015, 0.015, (ROD_CLEARANCE + MAIN_ROD_OFFSET) * 0.6, 12)
const pinMaterial = new MeshLambertMaterial({ color: '#FFC043' })

const connectingRodGeometry = new BoxGeometry(0.02, 0.05, ROD_LENGTH)
const rodMaterial = new MeshLambertMaterial({ color: '#61677F' })

interface WheelsProps {
  wheelX: number
  wheelY: number
  wheelFrontZ: number
  wheelRearZ: number
}

interface LocomotiveMechanismProps extends WheelsProps {
  vehicleId: string
  leftWheelFrontRef: React.RefObject<Mesh>
  leftWheelRearRef: React.RefObject<Mesh>
  rightWheelFrontRef: React.RefObject<Mesh>
  rightWheelRearRef: React.RefObject<Mesh>
}

export function LocomotiveMechanism({
  vehicleId,
  leftWheelFrontRef,
  leftWheelRearRef,
  rightWheelFrontRef,
  rightWheelRearRef,
  ...props
}: LocomotiveMechanismProps) {
  return (
    <>
      <LocomotiveMechanismSide
        vehicleId={vehicleId}
        side="right"
        wheelFrontRef={rightWheelFrontRef}
        wheelRearRef={rightWheelRearRef}
        {...props}
      />

      <LocomotiveMechanismSide
        vehicleId={vehicleId}
        side="left"
        wheelFrontRef={leftWheelFrontRef}
        wheelRearRef={leftWheelRearRef}
        {...props}
      />
    </>
  )
}

interface LocomotiveMechanismSideProps extends WheelsProps {
  vehicleId: string
  side: 'left' | 'right'
  wheelFrontRef: React.RefObject<Mesh>
  wheelRearRef: React.RefObject<Mesh>
}

function LocomotiveMechanismSide({
  vehicleId,
  side,
  wheelFrontRef,
  wheelRearRef,
  wheelX,
  wheelY,
  wheelFrontZ,
  wheelRearZ,
}: LocomotiveMechanismSideProps) {
  const wheelSpacing = Math.abs(wheelFrontZ - wheelRearZ)
  const couplingRodGeometry = useMemo(
    () => new BoxGeometry(0.02, 0.1, wheelSpacing + 0.08),
    [wheelSpacing],
  )

  const couplingRodRef = useRef<Group>(null!)
  const connectingRodRef = useRef<Group>(null!)
  const pinFrontRef = useRef<Mesh>(null!)
  const pinRearRef = useRef<Mesh>(null!)
  const accumulatedAngle = useRef(0)

  const isLeft = side === 'left'
  const signX = isLeft ? -1 : 1

  // Horizontal positions staggered to prevent the rods from touching
  const xCouplingRod = signX * (wheelX + ROD_CLEARANCE)
  const xConnectingRod = signX * (wheelX + ROD_CLEARANCE + MAIN_ROD_OFFSET)

  // Sides staggered by 90° (Math.PI / 2) as in real locomotives
  const phaseOffset = isLeft ? Math.PI / 2 : 0

  useFrame((_, delta) => {
    const network = useGameStore.getState().network
    const vehicle = getVehicleById(network, vehicleId) as Locomotive
    if (!vehicle) return

    const speed = vehicle.currentVelocity
    accumulatedAngle.current += speed * delta * LOCO_DEFAULT_SPEED
    const angle = accumulatedAngle.current + phaseOffset

    // 1. Rotate the model's wheels
    if (wheelFrontRef.current) wheelFrontRef.current.rotation.x = angle
    if (wheelRearRef.current) wheelRearRef.current.rotation.x = angle

    // 2. Calculate pin coordinates in the local Y-Z plane
    const pinY = Math.cos(angle) * PIN_OFFSET
    const pinZ = Math.sin(angle) * PIN_OFFSET

    const worldPinFrontZ = wheelFrontZ + pinZ
    const worldPinRearZ = wheelRearZ + pinZ
    const worldPinY = wheelY + pinY

    // Position both pins on their respective wheels
    if (pinFrontRef.current)
      pinFrontRef.current.position.set(xCouplingRod, worldPinY, worldPinFrontZ)
    if (pinRearRef.current) pinRearRef.current.position.set(xCouplingRod, worldPinY, worldPinRearZ)

    // 3. Coupling rod (connects the two driving wheels)
    if (couplingRodRef.current) {
      couplingRodRef.current.position.set(
        xCouplingRod,
        worldPinY,
        (worldPinFrontZ + worldPinRearZ) / 2,
      )
    }

    // 4. Crosshead kinematics
    const dy = pinY // difference in Y from the center
    const dz = Math.sqrt(ROD_LENGTH * ROD_LENGTH - dy * dy)
    const crossheadZ = worldPinFrontZ + dz

    // 5. Connecting rod (connects front pin and crosshead)
    if (connectingRodRef.current) {
      connectingRodRef.current.position.set(
        xConnectingRod,
        (worldPinY + wheelY) / 2,
        (worldPinFrontZ + crossheadZ) / 2,
      )
      // Math.atan2 ensures the inclination follows the pin height
      connectingRodRef.current.rotation.x = Math.atan2(dy, dz)
    }
  })

  return (
    <group>
      <mesh
        receiveShadow
        ref={pinFrontRef}
        geometry={pinGeometry}
        material={pinMaterial}
        rotation={[0, 0, Math.PI / 2]}
      />
      <mesh
        receiveShadow
        ref={pinRearRef}
        geometry={pinGeometry}
        material={pinMaterial}
        rotation={[0, 0, Math.PI / 2]}
      />

      <group ref={couplingRodRef}>
        <mesh receiveShadow geometry={couplingRodGeometry} material={rodMaterial} />
      </group>
      <group ref={connectingRodRef}>
        <mesh receiveShadow geometry={connectingRodGeometry} material={rodMaterial} />
      </group>
    </group>
  )
}
